import { Skeleton } from "@/components/ui/skeleton";
import type { DeniedCorrection, Correction } from "@/queries/formality-request";
import type { FormDetailResponse } from "@/types/entities/formality-request";
import { stripHtmlTags } from "@/utils/format";
import { FormCorrectionSectionList } from "./form-correction-list";
import { Button } from "@elaniintc/goes-design-system";
import { DenyGeneralReasonModal } from "../../../deny-general-modal";
import { useCorrectionForm } from "../context";

interface FormCorrectionEditProps {
  isFetchingRevisions?: boolean;
  visibleStepsWithIndices?: Array<{
    step: FormDetailResponse["steps"][number];
    stepNumber: number;
    visibleSections: FormDetailResponse["steps"][number]["sections"];
    sectionStartIndex: number;
    fieldStartIndex: number;
  }>;
  canSubmitCorrections?: boolean;
  isCancelled?: boolean;
  modalState?: "idle" | "general-reason";
  onSubmitCorrections: () => void;
  isLoadingUpdateRevision?: boolean;
  handleSubmitWithGeneralComment: (generalReason?: string) => void;
  onCloseModal: () => void;
}

export const FormCorrectionEdit = ({
  isFetchingRevisions = false,
  visibleStepsWithIndices = [],
  canSubmitCorrections,
  isCancelled,
  modalState,
  onSubmitCorrections,
  isLoadingUpdateRevision,
  handleSubmitWithGeneralComment,
  onCloseModal,
}: FormCorrectionEditProps) => {
  const {
    selectedRevision,
    selectedRevisionId,
    selectedGroupId,
    form,
    isLatestRevision,
    formalityRequest,
  } = useCorrectionForm();
  return (
    <div className="flex flex-col gap-6 w-full px-4 pt-4">
      {/* Comentarios del líder (deniedCorrections) - visible para el analista al re-hacer correcciones */}
      {isFetchingRevisions ? (
        <Skeleton className="w-full h-16 rounded-lg" />
      ) : (
        selectedRevision?.denied_corrections &&
        (selectedRevision?.denied_corrections?.length ?? 0) > 0 && (
          <div className="flex flex-col gap-2">
            {selectedRevision?.denied_corrections
              ?.filter((dc: DeniedCorrection) => {
                if (selectedGroupId && dc.group?.id) {
                  return dc.group.id === selectedGroupId;
                }
                return true;
              })
              .filter((dc: DeniedCorrection) => {
                // No mostrar si el analista ya envió una corrección más reciente que el rechazo del líder
                const leaderComment =
                  dc.comments.length > 0 ? dc.comments.at(-1) : null;
                if (!leaderComment?.created_at) return true;
                const leaderRejectTime = new Date(
                  leaderComment.created_at,
                ).getTime();
                const groupId = dc.group?.id;
                const hasNewerCorrection = (
                  selectedRevision?.corrections ?? []
                ).some((c: Correction) => {
                  const corrGroupId = c.group?.id;
                  const matchesGroup = groupId ? corrGroupId === groupId : true;
                  const corrTime = new Date(c.created_at ?? 0).getTime();
                  return matchesGroup && corrTime > leaderRejectTime;
                });
                return !hasNewerCorrection;
              })
              .map((dc: DeniedCorrection) => {
                const leaderComment =
                  dc.comments.length > 0 ? dc.comments.at(-1) : null;
                if (!leaderComment?.text) return null;
                return (
                  <div
                    key={dc.id}
                    className="bg-surface-danger-accent-quiet border rounded-lg border-contextual-danger-quiet p-2 flex flex-col gap-1"
                  >
                    <div className="flex items-center gap-2">
                      <h1 className="text-h1 text-heading text-size-body-sm leading-2xl">
                        Comentario de visto bueno
                      </h1>
                      {leaderComment.creator &&
                        typeof leaderComment.creator === "object" &&
                        "name" in leaderComment.creator && (
                          <span className="text-body text-xs">
                            —{" "}
                            {(
                              leaderComment.creator as {
                                name?: string;
                                last_name?: string;
                              }
                            ).name || ""}{" "}
                            {(
                              leaderComment.creator as {
                                name?: string;
                                last_name?: string;
                              }
                            ).last_name || ""}
                          </span>
                        )}
                    </div>
                    <p className="font-body text-body text-size-body whitespace-pre-line text-justify wrap-break-word min-w-0">
                      {stripHtmlTags(leaderComment.text)}
                    </p>
                  </div>
                );
              })}
          </div>
        )
      )}

      {visibleStepsWithIndices?.map(
        ({
          step,
          stepNumber,
          visibleSections,
          sectionStartIndex,
          fieldStartIndex,
        }) => (
          <FormCorrectionSectionList
            key={step.id}
            formAssigment={
              form?.form_assignments?.[form?.form_assignments?.length - 1]
            }
            stepTitle={step.name}
            stepNumber={stepNumber}
            data={visibleSections}
            currentGroupId={selectedGroupId}
            isReadOnly={isCancelled}
            sectionStartIndex={sectionStartIndex}
            fieldStartIndex={fieldStartIndex}
            applicant={{
              fullName: `${formalityRequest?.applicant?.first_name} ${formalityRequest?.applicant?.last_name}`,
              date:
                formalityRequest?.updated_at || formalityRequest?.created_at,
            }}
          />
        ),
      )}

      <div className="flex flex-col gap-2 items-end">
        {!isLatestRevision && selectedRevisionId && (
          <p className="text-warning text-sm">
            No puedes enviar correcciones de una revisión anterior. Selecciona
            la revisión más reciente.
          </p>
        )}
        <Button
          type="button"
          disabled={!canSubmitCorrections || !isLatestRevision || isCancelled}
          onClick={onSubmitCorrections}
          isLoading={isLoadingUpdateRevision && modalState === "idle"}
        >
          Enviar
        </Button>
      </div>

      {modalState === "general-reason" && (
        <DenyGeneralReasonModal
          isOpen={modalState === "general-reason"}
          onClose={onCloseModal}
          isLoading={isLoadingUpdateRevision}
          onAccept={(val) => {
            handleSubmitWithGeneralComment(val.description);
          }}
        />
      )}
    </div>
  );
};
